import React, { useId } from 'react';

// Hexagonal compliance stamp – four states (green tick / amber clock /
// red X / not-set dash) and three sizes (sm 28×32, md 48×56, lg 80×92).
//
// Visual treatment SPLITS BY PURPOSE per the recognition / compliance
// brief:
//   - Compliant (green) → METALLIC: dual-gradient + sheen overlay.
//     It's a positive achievement ("fully compliant – set up for
//     work") so the badge looks earned, matching the recognition
//     row's metallic medallion treatment.
//   - Expiring soon (amber) / Expired (red) / Not assessed (grey)
//     → FLAT semantic: tinted bg + matching stroke, no gradient,
//     no sheen. A warning must look like a warning; a metallic red
//     reads as rose-gold/copper (something earned), which is the
//     opposite of the alert we need.
//
// Icons:
//   - Tick (green) – kept
//   - Clock (amber) – replaces the previous Bang to read as
//     time-based urgency
//   - Cross (red) – kept
//   - Dash (not_set) – replaces the previous Question for a
//     quieter "no signal yet" look
//
// Visual class is deliberately distinct from the Eco-Engaged shield –
// hexagon = certified status, shield = earned achievement. Same
// visual weight, different visual languages, so the two can sit side
// by side in the Recognition grid without either diluting the other.
//
// Gradient IDs are namespaced via useId() so a page with multiple
// badges (e.g. the suppliers list) doesn't fail SVG-id-collision.
//
// Renders nothing for unknown/falsy state – mirrors the existing
// `{supplier.compliance_status && (...)}` gates so call sites don't
// need a separate null check.

const SIZES = {
  sm: { width: 28, height: 32 },
  md: { width: 48, height: 56 },
  lg: { width: 80, height: 92 },
};

// State configs. The `treatment` field drives the render path:
//   'metallic' – dual gradient (outer + inner) + sheen overlay polygon
//   'flat'     – single hex with tinted fill + matching stroke
//
// Metallic uses a 3-stop outer gradient (light top → mid saturation →
// darkest bottom) to read as "shaped metal," with an additional sheen
// triangle at the top half. Flat states deliberately skip both so a
// warning never reads like a reward.
const STATE = {
  green: {
    treatment: 'metallic',
    outerStops: ['#E8F6C2', '#A9D63F', '#5E8A14'],
    innerStops: ['#92D400', '#7AB200'],
    stroke: '#D8F29A',
    iconColor: '#1c2b06',
    label: 'Fully Compliant',
  },
  amber: {
    treatment: 'flat',
    hex: '#E89A2E',
    iconColor: '#E89A2E',
    label: 'Action Needed',
  },
  // Awaiting Fusion review – everything's submitted, nothing for the supplier
  // to do. Amber (the supplier's overall state while docs sit unapproved) but
  // an eye + "In Review" copy, so it doesn't read as "act now".
  review: {
    treatment: 'flat',
    hex: '#E89A2E',
    iconColor: '#E89A2E',
    label: 'In Review',
  },
  red: {
    treatment: 'solid',
    hex: '#E0290A',
    iconColor: '#ffffff',
    label: 'Outstanding',
  },
  not_set: {
    treatment: 'flat',
    hex: '#6c6f64',
    iconColor: '#9c9e94',
    label: 'Not Assessed',
  },
};

// All glyphs take `color` so the flat states render in their semantic
// hue (amber clock on amber bg, etc.) and the metallic green tick
// stays dark against the bright metal.
const Tick = ({ color }) => (
  <path d="M24,46 L34,56 L56,34" stroke={color} strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
);

// Clock for the Expiring Soon state – the urgency is time-based, so a
// clock reads as "act soon" more directly than the previous Bang.
const Clock = ({ color }) => (
  <g stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="40" cy="46" r="14" />
    <path d="M40,38 L40,46 L48,50" />
  </g>
);

const Cross = ({ color }) => (
  <g stroke={color} strokeWidth="5.5" strokeLinecap="round" fill="none">
    <line x1="30" y1="36" x2="50" y2="56" />
    <line x1="50" y1="36" x2="30" y2="56" />
  </g>
);

// Horizontal dash for Not Assessed – neutral, says "no signal yet."
// Quieter than the previous "?" which read as a posed question.
const Dash = ({ color }) => (
  <line x1="28" y1="46" x2="52" y2="46" stroke={color} strokeWidth="5" strokeLinecap="round" />
);

// Eye for In Review – "Fusion 2K is looking at it", no action from the supplier.
const Eye = ({ color }) => (
  <g stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24,46 Q40,33 56,46 Q40,59 24,46 Z" />
    <circle cx="40" cy="46" r="4.5" fill={color} stroke="none" />
  </g>
);

const SYMBOL = { green: Tick, amber: Clock, red: Cross, not_set: Dash, review: Eye };

// Derive the badge state from the supplier's overall state + document counts –
// shared so the dashboard card and the /portal/compliance card show the SAME
// badge. Mirrors the dashboard's card-state ladder: blocking → red, expiring →
// amber, awaiting-only → review (amber eye), else green.
export function badgeStateFor(state, counts = {}) {
  const c = counts || {};
  const blocking = (c.outstanding || 0) + (c.expired || 0) + (c.needsClarification || 0);
  if (state === 'green') return 'green';
  if (blocking > 0) return 'red';
  if ((c.expiringSoon || 0) > 0) return 'amber';
  if ((c.awaitingReview || 0) > 0) return 'review';
  return state === 'red' ? 'red' : state === 'amber' ? 'amber' : 'green';
}

// The short status word for a badge state – for labelling the badge where an
// icon alone is ambiguous (e.g. the supplier header, where a bare red hexagon
// sat next to "Risk: Low" and read as a contradiction).
export function badgeLabelFor(state) {
  return STATE[state]?.label || '';
}

// Tooltip copy. counts is optional – admin-side compliance_status
// from the suppliers table doesn't carry document counts, so we fall
// back to the bare state label. The supplier portal passes counts in
// from useSupplierCompliance.
const tooltipFor = (state, counts) => {
  if (state === 'green') return 'Fully Compliant – all documents up to date.';
  if (state === 'amber') {
    const n = counts ? (counts.expiringSoon || 0) + (counts.needsClarification || 0) : 0;
    return n > 0
      ? `Action Needed – ${n} document${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} attention.`
      : 'Action Needed – documents need attention.';
  }
  if (state === 'red') {
    const n = counts ? (counts.outstanding || 0) + (counts.expired || 0) : 0;
    return n > 0
      ? `Outstanding – ${n} document${n === 1 ? '' : 's'} missing or expired.`
      : 'Outstanding – documents missing or expired.';
  }
  if (state === 'review') {
    const n = counts ? (counts.awaitingReview || 0) : 0;
    return n > 0
      ? `In review – ${n} document${n === 1 ? '' : 's'} with Fusion 2K.`
      : 'In review – with Fusion 2K.';
  }
  if (state === 'not_set') return 'Not assessed – compliance hasn’t been reviewed yet.';
  return '';
};

export default function ComplianceBadge({
  state,
  size = 'md',
  counts = null,
  onClick = null,
  className = '',
  ariaLabel,
}) {
  // Normalised + null-safe lookup. The brief specifies only green/amber/red;
  // anything else collapses to no-render so call sites keep the current
  // `compliance_status && <Badge .../>` ergonomics.
  const lookup = STATE[state];
  const Symbol = SYMBOL[state];
  const dims = SIZES[size] || SIZES.md;
  const uid = useId();
  if (!lookup || !Symbol) return null;

  const tooltip = tooltipFor(state, counts);
  const labelText = ariaLabel || lookup.label;
  const outerId = `cb-outer-${uid}`;
  const innerId = `cb-inner-${uid}`;

  // Wrapped in a button when interactive – keeps focus / keyboard
  // accessibility for free. Static use renders as an inline span so
  // the badge can drop into a flex row without disturbing layout.
  const Wrapper = onClick ? 'button' : 'span';
  const wrapperProps = onClick
    ? { type: 'button', onClick, 'aria-label': labelText, title: tooltip, className: `inline-flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer ${className}` }
    : { 'aria-label': labelText, title: tooltip, className: `inline-flex items-center justify-center ${className}` };

  // Convert a hex like #E89A2E to rgba(...) at given opacity. Used by
  // the flat treatment for the tinted hex fill – keeps the bg at low
  // opacity so the badge integrates with whatever dark panel it sits
  // on without painting a heavy block of colour.
  const tintFill = (hex, alpha) => {
    const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return 'transparent';
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  };

  return (
    <Wrapper {...wrapperProps}>
      <svg
        role="img"
        aria-label={labelText}
        viewBox="0 0 80 92"
        width={dims.width}
        height={dims.height}
        style={{ display: 'block', flexShrink: 0 }}>
        {lookup.treatment === 'metallic' ? (
          <>
            <defs>
              {/* 3-stop outer gradient – light top, mid saturation,
                  darkest bottom. Inner is the simpler 2-stop the
                  symbol sits against. */}
              <linearGradient id={outerId} x1="0" y1="0" x2="0.5" y2="1">
                <stop offset="0%" stopColor={lookup.outerStops[0]} />
                <stop offset="45%" stopColor={lookup.outerStops[1]} />
                <stop offset="100%" stopColor={lookup.outerStops[2]} />
              </linearGradient>
              <linearGradient id={innerId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lookup.innerStops[0]} />
                <stop offset="100%" stopColor={lookup.innerStops[1]} />
              </linearGradient>
            </defs>
            <path d="M40,2 L74,22 L74,70 L40,90 L6,70 L6,22 Z" fill={`url(#${outerId})`} stroke={lookup.stroke} strokeWidth="1.5" />
            <path d="M40,8 L68,25 L68,67 L40,84 L12,67 L12,25 Z" fill={`url(#${innerId})`} />
            {/* Sheen – top-half polygon at 18% white. Catches light
                like a polished hexagon. */}
            <path d="M40,2 L74,22 L40,46 L6,22 Z" fill="#ffffff" opacity="0.18" />
          </>
        ) : lookup.treatment === 'solid' ? (
          /* Solid treatment – the blocker (Outstanding): a solid hex with
              a white glyph, the one heavy compliance signal (build #12). */
          <path d="M40,2 L74,22 L74,70 L40,90 L6,70 L6,22 Z" fill={lookup.hex} stroke={lookup.hex} strokeWidth="2.5" />
        ) : (
          /* Flat treatment – single hex with tinted fill + matching
              stroke. No gradient, no sheen – warnings stay warnings. */
          <path d="M40,2 L74,22 L74,70 L40,90 L6,70 L6,22 Z" fill={tintFill(lookup.hex, 0.16)} stroke={lookup.hex} strokeWidth="2.5" />
        )}
        <Symbol color={lookup.iconColor} />
      </svg>
    </Wrapper>
  );
}
